/**
 * @component ry-table
 * @category  display
 * @summary   Data table (Fomantic UI `.ui.table` equivalent). Wraps a `<table>` with sortable columns (click header to sort, click again to reverse, third click clears), zebra striping, hover row highlight, responsive collapse, and the standard Fomantic modifiers (compact, celled, striped, basic, very basic). Sort emits `ry:sort` with the active column + direction.
 *
 * Reference: Fomantic UI's `.ui.table`. The interactive layer (sorting) is
 * the only JS-driven feature — every visual variant is pure CSS via the
 * matching attribute. Authoring is identical to a native `<table>`.
 *
 * @attribute sortable     boolean   default=false   Enables click-to-sort on header cells with `[data-sort]`. Headers without that attribute remain non-interactive.
 * @attribute compact      boolean   default=false   Reduced row padding (Fomantic compact: 0.5em vertical).
 * @attribute very-compact boolean   default=false   Tightest padding (0.4em / 0.6em). Implies compactness on top of compact.
 * @attribute padded       boolean   default=false   Increased padding (1em / 1em) for readability.
 * @attribute very-padded  boolean   default=false   Maximum padding (1.5em / 1.5em).
 * @attribute celled       boolean   default=false   Renders vertical cell dividers (default has horizontal only).
 * @attribute striped      boolean   default=false   Alternating row background (zebra striping).
 * @attribute basic        boolean   default=false   Strips background + outer border + shadow; keeps cell borders.
 * @attribute very-basic   boolean   default=false   Same as `[basic]` plus removes inner cell borders. Implies `[basic]`.
 * @attribute selectable   boolean   default=false   Adds a hover highlight to body rows for selection affordance.
 * @attribute single-line  boolean   default=false   Cells don't wrap text (`white-space: nowrap`).
 * @attribute fixed        boolean   default=false   `table-layout: fixed` + ellipsize overflowing cells.
 * @attribute definition   boolean   default=false   First column rendered as label (heavier weight, light bg); first header cell rendered as transparent spacer. Fomantic .ui.definition.table parity.
 * @attribute inverted     boolean   default=false   Dark surface (per-table dark mode override).
 *
 * @attribute source       string?   Id of a `<ry-source>` providing rows. Its presence is what switches the table from DOM-as-truth to source-backed — there is no mode flag. Columns then need `<th data-field="key">`.
 * @attribute local-sort   boolean   default=false   Source mode only. Sort the rows currently held instead of asking the source. Off by default because sorting one fetched page and calling it sorted fails silently.
 * @attribute paginated    boolean   default=false   Source mode only. Send a `range` window sized by `[page-size]`. Without it the table asks for everything.
 * @attribute page-size    number    default=25      Records per page. Also the virtualization window size later.
 * @attribute pagination   string?   Id of a `<ry-pagination>` to drive and be driven by. The table converts its pages to contract ranges and reports the page count back.
 * @attribute refresh-interval  number?   Source mode only. Poll every N ms. A hidden tab does NOT poll (and catches up once on becoming visible), and a tick is skipped rather than queued while a request is in flight.
 * @attribute timeout      number?   Abort a request after this many ms. A hung request otherwise hangs forever AND wedges polling, since a tick is skipped while one is in flight. Surfaces as `ry:load-error` with a `RyTimeoutError` — a timeout is a failure the user must see, unlike a supersede.
 * @attribute retry        number?   default=0   Extra attempts after a RETRYABLE failure (5xx, 408, 429, timeout, dead network), with exponential backoff. A 404 is never retried — it will be a 404 next time too.
 * @attribute debounce     number?   default=0   Delay before `.filter` / `.query` changes fire a request. Paging and sorting are discrete clicks and always fire immediately.
 * @attribute append       boolean   default=false   Source mode only. A load ADDS to what's on
 *                           screen instead of replacing it — infinite scroll. `loadMore()` appends
 *                           regardless.
 * @attribute empty-text   string?   default="No results."   Message rendered in the empty row when a load returns zero rows.
 *
 * @property  rows          ReadonlyArray<RyRecord>   Rows from the last successful load. Empty in DOM-as-truth mode.
 * @property  total         number | undefined        Total records reported by the source, if it reported one.
 * @property  sourceBacked  boolean                   True when a source is attached — i.e. the DOM is derived output, not truth.
 * @property  paginationMode  'offset' | 'cursor' | 'none'   Learned from the source's last
 *                           response. `offset` (it sent `total`) allows jumping to any page and
 *                           sizing a scrollbar; `cursor` (it sent `cursor`) is forward-only.
 * @property  hasMore     boolean   True when a cursor-paginated source has more to give.
 * @property  serverRendered boolean                  True while the visible rows are the server's HTML rather than a fetch result.
 * @property  page          number                    1-based page. Setting it reloads.
 * @property  pageSize      number                    Reads `[page-size]`.
 * @property  filter        Record<string, unknown> | undefined   Field filters. Setting reloads and resets to page 1.
 * @property  query         string | undefined        Free-text search. Setting reloads and resets to page 1.
 *
 * @method    load()           Promise<void>   Fetch and render. No-op without a source. Aborts only THIS table's previous request.
 * @method    loadMore()       Promise<void>   Fetch the next page and APPEND it. The cursor-mode
 *                             counterpart to setting `page` — a cursor can't jump, so this is the
 *                             only motion available. No-op when `hasMore` is false.
 * @method    detachSource()   void   Drop the source and restore the authored `<tbody>` markup captured at mount.
 *
 * @event ry:sort
 *        detail: { column: string, direction: 'asc' | 'desc' | null }
 *        fires:  when a sortable header is clicked, BEFORE any rows move. `column` is the header's `[data-sort]` value (the consumer's chosen column identifier). `direction` cycles asc → desc → null (cleared) on repeated clicks. Cancelable — `preventDefault()` suppresses the local row reorder so a server-driven table can own the order itself. The header's `[data-direction]` marker updates either way, since it reflects the requested sort, not the achieved one.
 *
 * @event ry:load
 *        detail: { request: RyRequest }
 *        fires:  source mode only, before each request. Cancelable.
 *
 * @event ry:loaded
 *        detail: { rows, total?, cursor?, request }
 *        fires:  source mode only, after rows arrive and render.
 *
 * @event ry:load-error
 *        detail: { error, request }
 *        fires:  source mode only, when the fetcher throws or rejects. Aborts do NOT fire it.
 *
 * @keyboard  Sortable header cells get `tabindex="0"` + `role="button"`. Enter / Space activates sort.
 *
 * @state     Active sort column carries `[data-direction="asc|desc"]` on its `<th>`. Cleared headers have no direction attribute.
 *
 * @data      rows — via [source] pointing at a `<ry-source>`; DOM-as-truth without one
 *            emits: range (when [paginated]), sort, filter, query
 *            fields: read from `<th data-field="key">`, falling back to `[data-sort]`
 *
 * @content   A native `<table>` element with `<thead>` + `<tbody>`. Headers that should be sortable need `<th data-sort="columnName">`. The wrapper does NOT generate the table — author your own and let ry-table add behavior.
 *
 * @dom
 *   ry-table[sortable?][compact?][celled?][striped?][basic?][selectable?][source?]   data-ry-state?   aria-busy?
 *     > table   (authored)
 *       > thead
 *         > tr
 *           > th[data-sort?][data-direction?]   (sortable cells)
 *       > tbody
 *         > tr
 *           > td
 *
 * @gotcha  Sorting is client-side by default and operates on `tbody > tr`. For server-driven sorting, listen for `ry:sort`, call `event.preventDefault()`, and replace the rows yourself — the local reorder is suppressed and the header marker still updates.
 *
 * @gotcha  The third click CLEARS the sort and restores the original row order as authored. The order is captured the first time a sort runs from the unsorted state, so rows you inject while unsorted are captured too. Rows added *after* a sort began aren't in the snapshot; they're appended after the restored rows rather than dropped.
 *
 * @gotcha  Sort reads each cell's `textContent`, so a cell rendering formatted output (`<ry-format-number>`, `<ry-format-date>`, a currency string) sorts on the FORMATTED text. `parseFloat("$1,234")` is `NaN`, so the column silently falls back to `localeCompare` and "10" sorts before "9". Put the raw value in `data-sort-value` on the `<td>` to fix it: `<td data-sort-value="1234"><ry-format-number value="1234" style="currency"></ry-format-number></td>`.
 *
 * @gotcha  Sort compares numeric values when ALL cells in the column parse as numbers (via `parseFloat`, after `data-sort-value` is applied); otherwise falls back to localeCompare string sort. Mixing types in a column produces stable but possibly surprising ordering.
 *
 * @gotcha  Cursor and offset pagination are BOTH supported and the table detects which from the
 *          response — `total` means offset, `cursor` means cursor. They differ in what the UI can
 *          honestly offer: a cursor can't jump to page 5, so a numbered `<ry-pagination>` is
 *          HIDDEN in cursor mode rather than shown as a lie. Offer `loadMore()` instead. An
 *          absent `cursor` in the response is the end-of-results signal; there is no `hasMore`
 *          flag on the wire, because two ways to say the same thing is two ways to disagree.
 *
 * @gotcha  Calling `invalidate()` on the `<ry-source>` reloads EVERY widget pointing at it, including this one — that's the cheap tier of cross-widget reactivity, and the escape hatch for a stale server-rendered first page. It needs an actual `<ry-source>` element; a bare function assigned to `.source` has nothing to emit from.
 *
 * @gotcha  A source-backed table with rows ALREADY in `<tbody>` at mount does not fetch. Those rows are treated as the server-rendered page 1 — Rails/Django/Go printed them, the page works before any JS runs, and ry-ui takes over on the first sort / page / filter. An empty `<tbody>` means "nothing rendered, go fetch". The tradeoff: if that HTML is stale it stays stale until someone interacts, so call `load()` yourself when that matters. `serverRendered` tells you which case you're in.
 *
 * @gotcha  `data-ry-state` distinguishes `loading` (nothing on screen — render a skeleton) from `refreshing` (rows are already there — keep them, dim them). Sorting a populated table is a REFRESH, not a cold start; blanking it would be the wrong default. `aria-busy="true"` is set during both.
 *
 * @gotcha  Attaching a source makes the `<tbody>` DERIVED OUTPUT — every load replaces its
 *          children. The authored rows are snapshotted at mount and restored by
 *          `detachSource()`, so "remove the source and it falls back" is real, but anything
 *          you inject into `<tbody>` yourself after mount is lost on the next load.
 *
 * @gotcha  In source mode, sorting is DELEGATED — clicking a header refetches rather than
 *          reordering. `[local-sort]` opts back. The delegated request sends the header's
 *          `[data-sort]` value as the field, so `data-sort` remains the column identifier in
 *          both modes and `data-field` is only the record key.
 *
 * @gotcha  `[page-size]` is the table's, not the pager's. The table converts pages to the
 *          contract's `range` and sets the pager's `total` in PAGES from the source's total
 *          in RECORDS. Don't set the pager's `total` yourself in source mode.
 *
 * @gotcha  A `<ry-source>` usually has no `fetcher` yet when the table mounts, because the
 *          consumer's script runs after custom-element upgrade. The table waits for
 *          `ry:source-ready` instead — so the authored rows stay visible until the first
 *          real load, and you never have to reason about module order.
 *
 * @gotcha  `[basic]` and `[very-basic]` are AND-combinable (very-basic implies basic). `[celled]` and `[basic]` together is contradictory — the cascade decides which wins (typically celled, since it appears later in theme CSS).
 *
 * @gotcha  Headers without `[data-sort]` are NOT interactive even when the wrapper has `[sortable]`. To make a column unsortable, just omit `data-sort` from that `<th>`.
 *
 * @example basic
 * ```html
 * <ry-table>
 *   <table>
 *     <thead>
 *       <tr><th>Name</th><th>Email</th><th>Role</th></tr>
 *     </thead>
 *     <tbody>
 *       <tr><td>Jane Doe</td><td>jane@acme.com</td><td>Admin</td></tr>
 *       <tr><td>John Smith</td><td>john@acme.com</td><td>User</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example sortable
 * ```html
 * <ry-table sortable striped>
 *   <table>
 *     <thead>
 *       <tr>
 *         <th data-sort="name">Name</th>
 *         <th data-sort="age">Age</th>
 *         <th>Notes</th>
 *       </tr>
 *     </thead>
 *     <tbody>
 *       <tr><td>Alice</td><td>30</td><td>—</td></tr>
 *       <tr><td>Bob</td><td>25</td><td>—</td></tr>
 *       <tr><td>Carol</td><td>35</td><td>—</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example celled
 * ```html
 * <ry-table celled>
 *   <table>
 *     <thead><tr><th>Product</th><th>Price</th><th>Stock</th></tr></thead>
 *     <tbody>
 *       <tr><td>Widget</td><td>$10</td><td>42</td></tr>
 *       <tr><td>Gadget</td><td>$25</td><td>17</td></tr>
 *       <tr><td>Gizmo</td><td>$50</td><td>3</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example basic
 * ```html
 * <ry-table basic>
 *   <table>
 *     <thead><tr><th>Status</th><th>Description</th></tr></thead>
 *     <tbody>
 *       <tr><td>Active</td><td>Running normally</td></tr>
 *       <tr><td>Idle</td><td>Waiting for input</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example very-basic
 * ```html
 * <ry-table very-basic>
 *   <table>
 *     <thead><tr><th>Field</th><th>Value</th></tr></thead>
 *     <tbody>
 *       <tr><td>Name</td><td>Acme Inc.</td></tr>
 *       <tr><td>Founded</td><td>1985</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example compact
 * ```html
 * <ry-table compact celled>
 *   <table>
 *     <thead><tr><th>ID</th><th>Owner</th><th>Updated</th></tr></thead>
 *     <tbody>
 *       <tr><td>1</td><td>alice</td><td>2 min ago</td></tr>
 *       <tr><td>2</td><td>bob</td><td>1 hour ago</td></tr>
 *       <tr><td>3</td><td>carol</td><td>1 day ago</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example padded
 * ```html
 * <ry-table padded striped>
 *   <table>
 *     <thead><tr><th>Term</th><th>Definition</th></tr></thead>
 *     <tbody>
 *       <tr><td>Idempotent</td><td>An operation that produces the same result if applied multiple times.</td></tr>
 *       <tr><td>Memoize</td><td>To cache the result of a computation by its inputs.</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example state-rows
 * ```html
 * <ry-table celled>
 *   <table>
 *     <thead><tr><th>Build</th><th>Result</th></tr></thead>
 *     <tbody>
 *       <tr class="positive"><td>#1042</td><td>Passing</td></tr>
 *       <tr class="warning"><td>#1041</td><td>Flaky tests</td></tr>
 *       <tr class="negative"><td>#1040</td><td>Failing</td></tr>
 *       <tr class="active"><td>#1039</td><td>Selected</td></tr>
 *       <tr class="disabled"><td>#1038</td><td>Skipped</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example definition
 * ```html
 * <ry-table definition>
 *   <table>
 *     <thead><tr><th></th><th>Free</th><th>Pro</th><th>Enterprise</th></tr></thead>
 *     <tbody>
 *       <tr><td>Seats</td><td>1</td><td>10</td><td>Unlimited</td></tr>
 *       <tr><td>Storage</td><td>1 GB</td><td>100 GB</td><td>1 TB</td></tr>
 *       <tr><td>Support</td><td>Community</td><td>Email</td><td>24/7</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example single-line
 * ```html
 * <ry-table single-line celled>
 *   <table>
 *     <thead><tr><th>Path</th><th>Description</th></tr></thead>
 *     <tbody>
 *       <tr><td>/api/users</td><td>Returns the full list of users; very long description text that would normally wrap.</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example fixed
 * ```html
 * <ry-table fixed celled style="width: 400px;">
 *   <table>
 *     <thead><tr><th>Short</th><th>This header has more text and would wrap</th></tr></thead>
 *     <tbody>
 *       <tr><td>A</td><td>This cell would also wrap, but [fixed] ellipsizes overflow.</td></tr>
 *     </tbody>
 *   </table>
 * </ry-table>
 * ```
 *
 * @example with-footer
 * ```html
 * <ry-table celled>
 *   <table>
 *     <thead><tr><th>Item</th><th>Qty</th><th>Price</th></tr></thead>
 *     <tbody>
 *       <tr><td>Apples</td><td>3</td><td>$1.50</td></tr>
 *       <tr><td>Bread</td><td>1</td><td>$3.00</td></tr>
 *     </tbody>
 *     <tfoot>
 *       <tr><th>Total</th><th>4</th><th>$4.50</th></tr>
 *     </tfoot>
 *   </table>
 * </ry-table>
 * ```
 *
 * @tutorial mount-finds-inner-table
 * ```js
 * const el = mount(`
 *   <ry-table>
 *     <table>
 *       <thead><tr><th>A</th></tr></thead>
 *       <tbody><tr><td>1</td></tr></tbody>
 *     </table>
 *   </ry-table>
 * `);
 * // ry-table doesn't wrap or transform — it just acts on the authored <table>
 * await expectEqual(!!el.querySelector('table'), true);
 * await expectEqual(!!el.querySelector('thead > tr > th'), true);
 * ```
 *
 * @tutorial sortable-attribute-adds-roles-to-data-sort-headers
 * ```js
 * const el = mount(`
 *   <ry-table sortable>
 *     <table>
 *       <thead><tr>
 *         <th data-sort="name">Name</th>
 *         <th>Notes</th>
 *       </tr></thead>
 *       <tbody><tr><td>x</td><td>y</td></tr></tbody>
 *     </table>
 *   </ry-table>
 * `);
 * const sortable = el.querySelector('th[data-sort]');
 * const inert = el.querySelectorAll('th')[1];
 * await expectAttr(sortable, 'role', 'button', 'sortable th is keyboard-focusable');
 * await expectAttr(sortable, 'tabindex', '0');
 * await expectEqual(inert.hasAttribute('role'), false, 'non-sortable th has no role');
 * ```
 *
 * @tutorial click-sort-fires-ry-sort-with-asc-then-desc-then-null
 * ```js
 * const el = mount(`
 *   <ry-table sortable>
 *     <table>
 *       <thead><tr><th data-sort="x">X</th></tr></thead>
 *       <tbody>
 *         <tr><td>3</td></tr>
 *         <tr><td>1</td></tr>
 *         <tr><td>2</td></tr>
 *       </tbody>
 *     </table>
 *   </ry-table>
 * `);
 * const th = el.querySelector('th[data-sort]');
 * const fired = [];
 * el.addEventListener('ry:sort', (e) => fired.push(e.detail));
 * th.click();
 * await expectEqual(fired[0].direction, 'asc');
 * th.click();
 * await expectEqual(fired[1].direction, 'desc');
 * th.click();
 * await expectEqual(fired[2].direction, null, 'third click clears sort');
 * ```
 *
 * @tutorial sort-detail-includes-column-key
 * ```js
 * const el = mount(`
 *   <ry-table sortable>
 *     <table>
 *       <thead><tr>
 *         <th data-sort="name">Name</th>
 *         <th data-sort="email">Email</th>
 *       </tr></thead>
 *       <tbody>
 *         <tr><td>Bob</td><td>b@x</td></tr>
 *         <tr><td>Alice</td><td>a@x</td></tr>
 *       </tbody>
 *     </table>
 *   </ry-table>
 * `);
 * const fired = [];
 * el.addEventListener('ry:sort', (e) => fired.push(e.detail));
 * el.querySelectorAll('th[data-sort]')[1].click();
 * await expectEqual(fired[0].column, 'email');
 * ```
 *
 * @tutorial sort-reorders-rows
 * ```js
 * const el = mount(`
 *   <ry-table sortable>
 *     <table>
 *       <thead><tr><th data-sort="n">N</th></tr></thead>
 *       <tbody>
 *         <tr><td>3</td></tr>
 *         <tr><td>1</td></tr>
 *         <tr><td>2</td></tr>
 *       </tbody>
 *     </table>
 *   </ry-table>
 * `);
 * const th = el.querySelector('th');
 * th.click();
 * const rows = Array.from(el.querySelectorAll('tbody > tr'));
 * const values = rows.map((r) => r.textContent.trim());
 * await expectEqual(values[0], '1', 'ascending sort');
 * await expectEqual(values[1], '2');
 * await expectEqual(values[2], '3');
 * ```
 *
 * @tutorial clearing-restores-authored-order
 * ```js
 * // Third click doesn't just drop the marker — it puts the rows back the way
 * // they were authored.
 * const el = mount(`
 *   <ry-table sortable>
 *     <table>
 *       <thead><tr><th data-sort="n">N</th></tr></thead>
 *       <tbody>
 *         <tr><td>3</td></tr>
 *         <tr><td>1</td></tr>
 *         <tr><td>2</td></tr>
 *       </tbody>
 *     </table>
 *   </ry-table>
 * `);
 * const th = el.querySelector('th');
 * const order = () => Array.from(el.querySelectorAll('tbody > tr')).map(r => r.textContent.trim()).join(',');
 * th.click();                       // asc
 * await expectEqual(order(), '1,2,3');
 * th.click();                       // desc
 * await expectEqual(order(), '3,2,1');
 * th.click();                       // cleared
 * await expectEqual(order(), '3,1,2', 'back to the authored order');
 * await expectEqual(th.hasAttribute('data-direction'), false);
 * ```
 *
 * @tutorial preventdefault-suppresses-the-local-sort
 * ```js
 * // Wrong assumption: [sortable] means the wrapper always reorders rows, so a
 * // server-driven table has to avoid it. It doesn't — ry:sort fires BEFORE any
 * // row moves and is cancelable. The header still shows the requested
 * // direction, so you render the server's rows into a correctly-marked table.
 * const el = mount(`
 *   <ry-table sortable>
 *     <table>
 *       <thead><tr><th data-sort="n">N</th></tr></thead>
 *       <tbody>
 *         <tr><td>3</td></tr>
 *         <tr><td>1</td></tr>
 *         <tr><td>2</td></tr>
 *       </tbody>
 *     </table>
 *   </ry-table>
 * `);
 * el.addEventListener('ry:sort', (e) => e.preventDefault());
 * const th = el.querySelector('th');
 * th.click();
 * const order = Array.from(el.querySelectorAll('tbody > tr')).map(r => r.textContent.trim()).join(',');
 * await expectEqual(order, '3,1,2', 'rows untouched — the consumer owns ordering');
 * await expectAttr(th, 'data-direction', 'asc', 'but the header still shows the request');
 * ```
 *
 * @tutorial data-sort-value-beats-formatted-text
 * ```js
 * // Wrong assumption: sorting reads the underlying number. It reads
 * // textContent — so "$1,234" is not numeric and the column silently degrades
 * // to a string sort. data-sort-value carries the raw value.
 * const el = mount(`
 *   <ry-table sortable>
 *     <table>
 *       <thead><tr><th data-sort="amt">Amount</th></tr></thead>
 *       <tbody>
 *         <tr><td data-sort-value="9">$9.00</td></tr>
 *         <tr><td data-sort-value="1234">$1,234.00</td></tr>
 *         <tr><td data-sort-value="80">$80.00</td></tr>
 *       </tbody>
 *     </table>
 *   </ry-table>
 * `);
 * el.querySelector('th').click();
 * const order = Array.from(el.querySelectorAll('tbody > tr')).map(r => r.textContent.trim()).join(',');
 * await expectEqual(order, '$9.00,$80.00,$1,234.00', 'numeric sort via data-sort-value');
 * ```
 *
 * @tutorial source-mode-renders-rows
 * ```js
 * // Attaching a source flips the table to derived output; data-field maps
 * // record keys onto columns.
 * const wrap = mount(`
 *   <div>
 *     <ry-source id="t-src"></ry-source>
 *     <ry-table source="t-src">
 *       <table>
 *         <thead><tr><th data-field="name">Name</th><th data-field="age">Age</th></tr></thead>
 *         <tbody><tr><td>authored</td><td>0</td></tr></tbody>
 *       </table>
 *     </ry-table>
 *   </div>
 * `);
 * const table = wrap.querySelector('ry-table');
 * wrap.querySelector('ry-source').fetcher = () => ({ rows: [{ name: 'Ada', age: 36 }], total: 1 });
 * await table.load();
 * await expectEqual(table.querySelector('tbody').textContent.trim(), 'Ada36');
 * await expectEqual(table.sourceBacked, true);
 * await expectAttr(table, 'data-ry-state', 'ready');
 * ```
 *
 * @tutorial invalidate-reloads-every-consumer
 * ```js
 * // Cross-widget reactivity without a cache: one call, every reader refreshes.
 * const wrap = mount(`
 *   <div>
 *     <ry-source id="t-inv"></ry-source>
 *     <ry-table source="t-inv">
 *       <table><thead><tr><th data-field="n">N</th></tr></thead><tbody></tbody></table>
 *     </ry-table>
 *   </div>
 * `);
 * const table = wrap.querySelector('ry-table');
 * const src = wrap.querySelector('ry-source');
 * let value = 'a';
 * src.fetcher = () => ({ rows: [{ n: value }] });
 * await table.load();
 * await expectEqual(table.querySelector('tbody').textContent, 'a');
 * value = 'b';
 * src.invalidate();
 * await sleep(0);
 * await expectEqual(table.querySelector('tbody').textContent, 'b', 'invalidate() refreshed it');
 * ```
 *
 * @tutorial source-mode-delegates-sorting
 * ```js
 * // Wrong assumption: [sortable] always reorders rows. With a source attached
 * // it REFETCHES instead, sending the sort to the backend — because reordering
 * // only the page you're holding and calling it sorted is silently wrong.
 * const wrap = mount(`
 *   <div>
 *     <ry-source id="t-src2"></ry-source>
 *     <ry-table source="t-src2" sortable>
 *       <table>
 *         <thead><tr><th data-field="name" data-sort="name">Name</th></tr></thead>
 *         <tbody></tbody>
 *       </table>
 *     </ry-table>
 *   </div>
 * `);
 * const table = wrap.querySelector('ry-table');
 * const seen = [];
 * wrap.querySelector('ry-source').fetcher = (req) => {
 *   seen.push(req.sort);
 *   return { rows: [{ name: 'z' }, { name: 'a' }] };
 * };
 * await table.load();
 * table.querySelector('th[data-sort]').click();
 * await sleep(0);
 * await expectEqual(JSON.stringify(seen[seen.length - 1]), '[{"field":"name","dir":"asc"}]');
 * await expectEqual(table.querySelector('tbody').textContent.trim(), 'za', 'rows are the source order, not reordered');
 * ```
 *
 * @tutorial keyboard-activates-sort
 * ```js
 * const el = mount(`
 *   <ry-table sortable>
 *     <table>
 *       <thead><tr><th data-sort="x">X</th></tr></thead>
 *       <tbody><tr><td>1</td></tr></tbody>
 *     </table>
 *   </ry-table>
 * `);
 * const th = el.querySelector('th');
 * const fired = [];
 * el.addEventListener('ry:sort', () => fired.push(1));
 * th.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(fired.length, 1, 'Enter activates sort');
 * th.dispatchEvent(new KeyboardEvent('keydown', { key: ' ', bubbles: true }));
 * await expectEqual(fired.length, 2, 'Space activates sort');
 * ```
 *
 * @tutorial non-sortable-headers-have-no-listeners
 * ```js
 * const el = mount(`
 *   <ry-table sortable>
 *     <table>
 *       <thead><tr><th>Plain</th></tr></thead>
 *       <tbody><tr><td>x</td></tr></tbody>
 *     </table>
 *   </ry-table>
 * `);
 * const th = el.querySelector('th');
 * const fired = [];
 * el.addEventListener('ry:sort', () => fired.push(1));
 * th.click();
 * await expectEqual(fired.length, 0, 'no [data-sort] = no sort');
 * ```
 *
 * @tutorial sortable-attribute-required
 * ```js
 * // Wrong assumption: data-sort on the th is enough. The wrapper needs
 * // [sortable] to wire up listeners.
 * const el = mount(`
 *   <ry-table>
 *     <table>
 *       <thead><tr><th data-sort="x">X</th></tr></thead>
 *       <tbody><tr><td>1</td></tr></tbody>
 *     </table>
 *   </ry-table>
 * `);
 * const th = el.querySelector('th[data-sort]');
 * await expectEqual(th.hasAttribute('role'), false, 'no role without [sortable]');
 * ```
 *
 * @see ry-list
 * @see ry-card
 */

import { RyElement } from '../core/ry-element.js';
import { RyLoaderRunner } from '../core/ry-loader-runner.js';
import { resolveSource, type ResolvedSource } from './ry-source.js';
import { paginationMode } from '../core/ry-rows.js';
import type { RyRecord, RyDataState, RySortSpec } from '../core/ry-data.js';

type SortDirection = 'asc' | 'desc' | null;

interface SortDetail {
  column: string;
  direction: SortDirection;
}

const DEFAULT_PAGE_SIZE = 25;

export class RyTable extends RyElement {
  static observedAttributes = ['refresh-interval'] as const;

  #numAttr(name: string): number {
    const raw = Number(this.getAttribute(name));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  }

  #activeColumn: string | null = null;
  #activeDirection: SortDirection = null;

  // ─── source mode ──────────────────────────────────────────────────
  // All null / empty in DOM-as-truth mode, which is the default and stays
  // byte-for-byte the behavior it always had.
  #source: ResolvedSource | null = null;
  #runner = new RyLoaderRunner(this, {
    baseMeta: () => this.#source?.baseMeta ?? {},
    timeoutMs: () => this.#numAttr('timeout'),
    retries: () => this.#numAttr('retry'),
  });
  #rows: ReadonlyArray<RyRecord> = [];
  #total: number | undefined = undefined;
  #page = 1;
  /** Authored <tbody> rows, kept so removing the source restores the markup. */
  #authoredRows: HTMLTableRowElement[] | null = null;
  /** True while the rows on screen are the server's HTML, not a fetch result. */
  #serverRendered = false;
  /** Free-text + field filters. Properties, not attributes — there's no filter UI yet. */
  #filter: Record<string, unknown> | undefined = undefined;
  #query: string | undefined = undefined;
  /** Cursor from the last response. Undefined means "no more" or offset mode. */
  #cursor: string | undefined = undefined;
  #mode: 'offset' | 'cursor' | 'none' = 'none';

  /**
   * Row order as authored, captured the first time a sort runs from the
   * unsorted state. Restored when the sort is cleared. Null while unsorted, so
   * rows injected before the first sort are captured too.
   */
  #originalOrder: HTMLTableRowElement[] | null = null;

  setup(): void {
    if (this.hasAttribute('sortable')) {
      this.#wireSortableHeaders();
    }
    this.#connectSource();
    this.#connectPagination();
  }

  teardown(): void {
    this.#runner.stopPolling();
    this.#runner.cancelDebounce();
    this.#runner.abort();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue || !this.isConnected) return;
    if (name === 'refresh-interval') this.#syncPolling();
  }

  // ──────────────────────────────────────────────────────────────────
  // Source mode — public API
  // ──────────────────────────────────────────────────────────────────

  /** Rows from the last successful load. Empty in DOM-as-truth mode. */
  get rows(): ReadonlyArray<RyRecord> {
    return this.#rows;
  }

  /** Total matching records reported by the source, if it reported one. */
  get total(): number | undefined {
    return this.#total;
  }

  /** True when a source is attached — i.e. the DOM is derived output, not truth. */
  get sourceBacked(): boolean {
    return this.#source !== null;
  }

  get page(): number {
    return this.#page;
  }

  set page(value: number) {
    const next = Math.max(1, Math.floor(value));
    if (next === this.#page) return;
    this.#page = next;
    void this.load();
  }

  get pageSize(): number {
    const raw = Number(this.getAttribute('page-size'));
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : DEFAULT_PAGE_SIZE;
  }

  get filter(): Record<string, unknown> | undefined {
    return this.#filter;
  }

  set filter(value: Record<string, unknown> | undefined) {
    this.#filter = value;
    this.#page = 1;
    this.#debouncedLoad();
  }

  get query(): string | undefined {
    return this.#query;
  }

  set query(value: string | undefined) {
    this.#query = value;
    this.#page = 1;
    this.#debouncedLoad();
  }

  /**
   * Fetch and render. No-op in DOM-as-truth mode. Aborts this table's own
   * previous request — not the source's, and not any other widget's.
   */
  /**
   * Which pagination the source is using, learned from its last response. Both
   * are supported; they differ in what the UI can offer.
   *
   *   offset — `total` present. Jump to any page, size a scrollbar.
   *   cursor — `cursor` present. Forward-only; no page count, no thumb.
   *   none   — the source returned everything.
   */
  get paginationMode(): 'offset' | 'cursor' | 'none' {
    return this.#mode;
  }

  /** True when a cursor-paginated source has more to give. */
  get hasMore(): boolean {
    return this.#mode === 'cursor' && this.#cursor !== undefined;
  }

  /**
   * Fetch the next page and APPEND it. The cursor-mode counterpart to setting
   * `page` — a cursor can't jump, so "load more" is the only motion available.
   * No-op when there's nothing more.
   */
  async loadMore(): Promise<void> {
    if (!this.hasMore) return;
    await this.load({ append: true });
  }

  async load(options: { append?: boolean } = {}): Promise<void> {
    // Re-resolve every time: ResolvedSource is a SNAPSHOT, and a provider's
    // `fetcher` is usually assigned after this table mounted. Caching the
    // snapshot meant a fetcher set later was invisible forever.
    if (!this.#source) return;
    const source = resolveSource(this) ?? this.#source;
    this.#source = source;
    if (!source.fetcher) return;

    // Cursor and offset are mutually exclusive per request: send the cursor
    // when continuing a cursor-paginated run, a range otherwise.
    const continuing = options.append === true && this.#cursor !== undefined;
    const response = await this.#runner.run(source.fetcher, {
      cursor: continuing ? this.#cursor : undefined,
      range: !continuing && this.hasAttribute('paginated')
        ? { start: (this.#page - 1) * this.pageSize, end: this.#page * this.pageSize }
        : undefined,
      sort: this.#sortSpec(),
      filter: this.#filter,
      query: this.#query,
    });
    if (!response) return;   // superseded, cancelled, or failed — runner set the state

    this.#serverRendered = false;
    this.#mode = paginationMode(response);
    // Absent cursor is the end-of-results signal; there is no hasMore flag.
    this.#cursor = response.cursor;
    const append = options.append === true || this.hasAttribute('append');
    this.#rows = append ? [...this.#rows, ...response.rows] : response.rows;
    this.#total = response.total;
    this.#renderRows(this.#rows);
    this.#syncPagination();
  }

  // ──────────────────────────────────────────────────────────────────
  // Source mode — wiring
  // ──────────────────────────────────────────────────────────────────

  #connectSource(): void {
    const resolved = resolveSource(this);
    if (!resolved) return;
    this.#source = resolved;
    RyLoaderRunner.initState(this);

    // Snapshot the authored rows BEFORE the first render replaces them, so
    // detaching the source can put the markup back (data-contract D1).
    const tbody = this.$<HTMLTableSectionElement>('tbody');
    if (tbody) {
      this.#authoredRows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>(':scope > tr'));
    }

    // E7: authored rows ARE page 1. If the server already rendered them, don't
    // throw that work away and refetch the same records with a flash — the page
    // is useful before any JS runs, and we take over on the first interaction.
    // An empty <tbody> means "nothing rendered, go fetch".
    if (this.#authoredRows && this.#authoredRows.length > 0) {
      this.#serverRendered = true;
      this.state = 'ready';
      this.#wireLiveness(resolved);
      return;
    }

    this.#wireLiveness(resolved);

    if (resolved.fetcher) {
      void this.load();
      return;
    }
    // The provider exists but its fetcher hasn't been assigned yet — normal,
    // since the consumer's script usually runs after custom-element upgrade.
    // Wait for it rather than making authors reason about module order.
    const provider = resolved.element;
    if (provider) {
      this.on(provider, 'ry:source-ready', () => {
        this.#source = resolveSource(this);
        void this.load();
      }, { once: true });
    }
  }

  /** True when the initial rows came from the server's HTML rather than a fetch. */
  get serverRendered(): boolean {
    return this.#serverRendered;
  }

  /**
   * Wire the two reactivity paths. Both apply even to a server-rendered table
   * that will never auto-load — a stale first page is precisely the case
   * invalidation and polling exist for.
   */
  #wireLiveness(resolved: ResolvedSource): void {
    if (resolved.element) {
      this.on(resolved.element, 'ry:invalidate', () => { void this.load(); });
    }
    this.#syncPolling();
  }

  /**
   * Filter and query changes go through `[debounce]` — those are the ones a
   * user drives keystroke by keystroke. Paging and sorting are discrete clicks
   * and fire immediately.
   */
  #debouncedLoad(): void {
    const ms = this.#numAttr('debounce');
    if (ms > 0) this.#runner.debounce(ms, () => { void this.load(); });
    else void this.load();
  }

  #syncPolling(): void {
    const ms = Number(this.getAttribute('refresh-interval'));
    if (this.#source && Number.isFinite(ms) && ms > 0) {
      this.#runner.startPolling(ms, () => { void this.load(); });
    } else {
      this.#runner.stopPolling();
    }
  }

  /**
   * Detach the source and put the authored markup back. This is what makes
   * "remove the source and it falls back" true rather than aspirational.
   */
  detachSource(): void {
    if (!this.#source) return;
    this.#runner.abort();
    this.#source = null;
    (this as { source?: unknown }).source = undefined;
    this.removeAttribute('source');
    this.#rows = [];
    this.#total = undefined;
    const tbody = this.$<HTMLTableSectionElement>('tbody');
    if (tbody && this.#authoredRows) {
      tbody.replaceChildren(...this.#authoredRows);
    }
    this.removeAttribute('data-ry-state');
  }

  #connectPagination(): void {
    const pager = this.#pager();
    if (!pager) return;
    this.on(pager, 'ry:change', (e) => {
      // Cast through unknown: the global HTMLElementEventMap declares ry:change
      // as ChangeDetail {value,label}, but ry-pagination emits {page,oldPage}.
      // The global map carries one shape for an event many components emit with
      // different shapes — see the note in docs/arch/data-contract.md.
      const detail = (e as unknown as CustomEvent<{ page: number }>).detail;
      if (typeof detail?.page === 'number') this.page = detail.page;
    });
  }

  #pager(): HTMLElement | null {
    const id = this.getAttribute('pagination');
    if (!id) return null;
    return this.ownerDocument.getElementById(id);
  }

  /**
   * The contract speaks ranges; ry-pagination speaks pages. This is the one
   * place the conversion lives, in both directions.
   */
  #syncPagination(): void {
    const pager = this.#pager() as (HTMLElement & { total?: number; page?: number }) | null;
    if (!pager) return;
    if (this.#mode === 'cursor') {
      // A cursor can't jump to page 5, so a numbered pager would be a lie.
      // Hide it and let the consumer offer loadMore() instead.
      pager.setAttribute('hidden', '');
      return;
    }
    pager.removeAttribute('hidden');
    if (this.#total !== undefined) {
      pager.total = Math.max(1, Math.ceil(this.#total / this.pageSize));
    }
    if (pager.page !== this.#page) pager.page = this.#page;
  }

  /** Current sort as the contract's array form. Undefined when nothing is sorted. */
  #sortSpec(): RySortSpec[] | undefined {
    if (!this.#activeColumn || !this.#activeDirection) return undefined;
    return [{ field: this.#activeColumn, dir: this.#activeDirection }];
  }

  /** Column keys, in header order, from `<th data-field>` (falling back to `data-sort`). */
  #fields(): string[] {
    return this.$$<HTMLTableCellElement>('thead th').map(
      (th) => th.dataset.field ?? th.dataset.sort ?? ''
    );
  }

  #renderRows(rows: ReadonlyArray<RyRecord>): void {
    const tbody = this.$<HTMLTableSectionElement>('tbody');
    if (!tbody) return;
    const fields = this.#fields();

    if (rows.length === 0) {
      tbody.replaceChildren(this.#messageRow('empty', this.getAttribute('empty-text') ?? 'No results.', fields.length));
      return;
    }

    const frag = this.ownerDocument.createDocumentFragment();
    for (const row of rows) {
      const tr = this.ownerDocument.createElement('tr');
      tr.setAttribute('data-ry-target', 'row');
      const id = row['id'];
      if (id !== undefined && id !== null) tr.dataset.ryId = String(id);
      for (const field of fields) {
        const td = this.ownerDocument.createElement('td');
        td.setAttribute('data-ry-target', 'cell');
        const value = field ? row[field] : undefined;
        td.textContent = value === undefined || value === null ? '' : String(value);
        // Give local-sort the raw value so it doesn't re-derive it from text.
        if (typeof value === 'number' || typeof value === 'boolean') {
          td.dataset.sortValue = String(value);
        }
        tr.appendChild(td);
      }
      frag.appendChild(tr);
    }
    tbody.replaceChildren(frag);
    // Rendered rows supersede any authored order snapshot.
    this.#originalOrder = null;
  }

  #messageRow(target: 'empty' | 'error', text: string, colspan: number): HTMLTableRowElement {
    const tr = this.ownerDocument.createElement('tr');
    tr.setAttribute('data-ry-target', target);
    const td = this.ownerDocument.createElement('td');
    td.colSpan = Math.max(1, colspan);
    td.textContent = text;
    tr.appendChild(td);
    return tr;
  }

  /** `state` is narrowed for source mode; DOM mode never sets it. */
  override get state(): RyDataState {
    return (this.dataset.ryState as RyDataState | undefined) ?? 'idle';
  }

  override set state(value: RyDataState) {
    super.state = value;
  }

  #wireSortableHeaders(): void {
    const headers = this.$$<HTMLTableCellElement>('th[data-sort]');
    headers.forEach((th) => {
      th.setAttribute('role', 'button');
      if (!th.hasAttribute('tabindex')) th.setAttribute('tabindex', '0');
      this.on(th, 'click', () => this.#handleSort(th));
      this.on(th, 'keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.#handleSort(th);
        }
      });
    });
  }

  #handleSort(th: HTMLTableCellElement): void {
    const column = th.dataset.sort ?? '';
    let direction: SortDirection;

    if (this.#activeColumn !== column) {
      direction = 'asc';
    } else if (this.#activeDirection === 'asc') {
      direction = 'desc';
    } else if (this.#activeDirection === 'desc') {
      direction = null;
    } else {
      direction = 'asc';
    }

    // Emit BEFORE touching rows so a server-driven table can preventDefault()
    // and own the ordering itself. The header marker updates either way — it
    // reflects the sort that was REQUESTED, not the one we happened to apply.
    const proceed = this.emit<SortDetail>('sort', { column, direction });

    // Clear previous active header markers
    this.$$<HTMLTableCellElement>('th[data-sort]').forEach((h) => {
      h.removeAttribute('data-direction');
    });

    if (direction !== null) {
      th.setAttribute('data-direction', direction);
    }

    this.#activeColumn = direction === null ? null : column;
    this.#activeDirection = direction;

    if (!proceed) return;

    // With a source attached, sorting is DELEGATED by default. Reordering only
    // the rows currently in hand and presenting that as "sorted" is silently
    // wrong; [local-sort] is how you ask for it by name.
    if (this.#source && !this.hasAttribute('local-sort')) {
      this.#page = 1;
      void this.load();
      return;
    }

    this.#sortRows(th, direction);
  }

  #sortRows(th: HTMLTableCellElement, direction: SortDirection): void {
    const tbody = this.$<HTMLTableSectionElement>('tbody');
    if (!tbody) return;

    const rows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>(':scope > tr'));

    if (direction === null) {
      this.#restoreOrder(tbody, rows);
      return;
    }

    // Snapshot the authored order the first time we sort away from it.
    this.#originalOrder ??= rows.slice();

    const columnIndex = Array.from(th.parentElement?.children ?? []).indexOf(th);
    if (columnIndex < 0) return;

    const valueOf = (row: HTMLTableRowElement): string => {
      const cell = row.children[columnIndex] as HTMLElement | undefined;
      if (!cell) return '';
      // `data-sort-value` is the escape hatch for formatted cells — a
      // <ry-format-number> renders "$1,234", which parseFloat can't read.
      return (cell.dataset.sortValue ?? cell.textContent ?? '').trim();
    };

    const allNumeric = rows.every((row) => !isNaN(parseFloat(valueOf(row))));

    rows.sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      const cmp = allNumeric ? parseFloat(av) - parseFloat(bv) : av.localeCompare(bv);
      return direction === 'asc' ? cmp : -cmp;
    });

    rows.forEach((row) => tbody.appendChild(row));
  }

  /**
   * Put rows back in authored order. Rows added after the snapshot aren't in
   * it — append them rather than drop them.
   */
  #restoreOrder(tbody: HTMLTableSectionElement, current: HTMLTableRowElement[]): void {
    if (!this.#originalOrder) return;
    const snapshot = new Set(this.#originalOrder);
    for (const row of this.#originalOrder) {
      if (row.parentElement === tbody) tbody.appendChild(row);
    }
    for (const row of current) {
      if (!snapshot.has(row)) tbody.appendChild(row);
    }
    this.#originalOrder = null;
  }
}

customElements.define('ry-table', RyTable);
